import {
  Board,
  BoardSide,
  CardChoice,
  DoubleSixIndex,
  EmptyDomino,
  GameEvent,
  GameMode,
  GameStateStr,
  PartnerPose,
  emptyBoard,
} from "./types";
import { getSuits, isDouble } from "./dominoMap";

export interface Player {
  getHand(): number[];
  setHand(hand: number[]): void;
  playCard(event: GameEvent): Promise<CardChoice>;
  poseCard(): Promise<number>;
  poseDoubleSix(): Promise<number>;
  getName(): string;
}

function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleInPlace<T>(arr: T[], rng: () => number): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

function countDoubles(hand: number[]): number {
  let c = 0;
  for (const d of hand) if (d < 28 && isDouble(d)) c++;
  return c;
}

export class LocalGame {
  gameType: GameMode;
  randomSeed: number;
  players: [Player, Player, Player, Player];
  board: Board = emptyBoard();
  state: GameStateStr = "dsp";
  playerTurn = 0;
  lastWinner = 0;
  playerWins: [number, number, number, number] = [0, 0, 0, 0];
  numRounds = 0;
  poseModeDoubleSix = false;
  partnerPosesAllowed = 0;
  lastDominoPlayed = 0;
  passCount = 0;
  maxWins = 6;

  constructor(
    players: [Player, Player, Player, Player],
    randomSeed: number,
    gameType: GameMode
  ) {
    this.players = players;
    this.randomSeed = randomSeed | 0 || 1;
    this.gameType = gameType === "partner" ? "partner" : "cutthroat";
  }

  private hasExcessiveDoubles(all: number[]): boolean {
    for (let i = 0; i < 4; i++) {
      if (countDoubles(all.slice(i * 7, i * 7 + 7)) >= 3) return true;
    }
    return false;
  }

  shuffleAndDistribute(): void {
    const maxReshuffles = 5;
    const reshuffleChance = 0.8;
    for (let attempt = 0; attempt <= maxReshuffles; attempt++) {
      const all: number[] = [];
      for (let i = 0; i < 28; i++) all.push(i);
      const rng = mulberry32(this.randomSeed + this.numRounds + attempt * 1000);
      shuffleInPlace(all, rng);
      if (
        attempt === maxReshuffles ||
        !this.hasExcessiveDoubles(all) ||
        rng() >= reshuffleChance
      ) {
        this.players[0].setHand(all.slice(0, 7));
        this.players[1].setHand(all.slice(7, 14));
        this.players[2].setHand(all.slice(14, 21));
        this.players[3].setHand(all.slice(21, 28));
        return;
      }
    }
  }

  private findDoubleSixPlayer(): number {
    for (let i = 0; i < 4; i++) {
      if (this.players[i].getHand().includes(DoubleSixIndex)) return i;
    }
    return -1;
  }

  private advancePlayerTurn(): void {
    this.playerTurn = (this.playerTurn + 1) % 4;
  }

  private updateBoard(card: number, side: BoardSide): void {
    const [s1, s2] = getSuits(card);
    if (side === "posed") {
      this.board.cardPosed = true;
      this.board.posedCard = card;
      this.board.leftSuit = s1;
      this.board.rightSuit = s2;
      return;
    }
    if (side === "left") {
      if (this.board.leftSuit === s1) this.board.leftSuit = s2;
      else if (this.board.leftSuit === s2) this.board.leftSuit = s1;
      else return;
      this.board.leftBoard.push(card);
      return;
    }
    if (this.board.rightSuit === s1) this.board.rightSuit = s2;
    else if (this.board.rightSuit === s2) this.board.rightSuit = s1;
    else return;
    this.board.rightBoard.push(card);
  }

  private removeCardFromPlayer(card: number, player: number): void {
    const hand = this.players[player].getHand().slice();
    for (let i = 0; i < hand.length; i++) {
      if (hand[i] === card) hand[i] = EmptyDomino;
    }
    this.players[player].setHand(hand);
  }

  private playerHasCard(player: number, card: number): boolean {
    return this.players[player].getHand().some((d) => d === card);
  }

  private canPlayCard(player: number): boolean {
    const hand = this.players[player].getHand();
    for (const d of hand) {
      if (d === EmptyDomino) continue;
      const [s1, s2] = getSuits(d);
      if (
        s1 === this.board.leftSuit ||
        s1 === this.board.rightSuit ||
        s2 === this.board.leftSuit ||
        s2 === this.board.rightSuit
      ) {
        return true;
      }
    }
    return false;
  }

  private numCardsRemaining(player: number): number {
    let c = 0;
    for (const d of this.players[player].getHand()) {
      if (d !== EmptyDomino && d < 28) c++;
    }
    return c;
  }

  private allCounts(): [number, number, number, number] {
    return [
      this.numCardsRemaining(0),
      this.numCardsRemaining(1),
      this.numCardsRemaining(2),
      this.numCardsRemaining(3),
    ];
  }

  private playerHandPipCount(player: number): number {
    let c = 0;
    for (const d of this.players[player].getHand()) {
      if (d !== EmptyDomino && d < 28) {
        const [s1, s2] = getSuits(d);
        c += s1 + s2;
      }
    }
    return c;
  }

  private wonKey(card: number): boolean {
    if (isDouble(card)) return false;
    const [k1, k2] = getSuits(card);
    const countOf = (suit: number): number => {
      let c = 0;
      const checkCard = (idx: number) => {
        const [s1, s2] = getSuits(idx);
        if (s1 === suit || s2 === suit) c++;
      };
      checkCard(this.board.posedCard);
      this.board.leftBoard.forEach(checkCard);
      this.board.rightBoard.forEach(checkCard);
      return c;
    };
    if (countOf(k1) !== 7 || countOf(k2) !== 7) return false;
    return (
      (this.board.leftSuit === k1 && this.board.rightSuit === k1) ||
      (this.board.leftSuit === k2 && this.board.rightSuit === k2)
    );
  }

  private isGameBlocked(): boolean {
    if (this.board.leftSuit !== this.board.rightSuit) return false;
    const suit = this.board.leftSuit;
    let count = 0;
    const inc = (idx: number) => {
      const [s1, s2] = getSuits(idx);
      if (s1 === suit || s2 === suit) count++;
    };
    inc(this.board.posedCard);
    this.board.leftBoard.forEach(inc);
    this.board.rightBoard.forEach(inc);
    return count === 7;
  }

  private playerNames(): [string, string, string, string] {
    return [
      this.players[0].getName(),
      this.players[1].getName(),
      this.players[2].getName(),
      this.players[3].getName(),
    ];
  }

  private allHands(): [number[], number[], number[], number[]] {
    return [
      this.players[0].getHand().slice(),
      this.players[1].getHand().slice(),
      this.players[2].getHand().slice(),
      this.players[3].getHand().slice(),
    ];
  }

  private boardCopy(): Board {
    return {
      cardPosed: this.board.cardPosed,
      posedCard: this.board.posedCard,
      leftSuit: this.board.leftSuit,
      rightSuit: this.board.rightSuit,
      leftBoard: this.board.leftBoard.slice(),
      rightBoard: this.board.rightBoard.slice(),
    };
  }

  private baseEvent(): GameEvent {
    return {
      eventType: "null",
      player: this.playerTurn,
      playersCardsRemaining: this.allCounts(),
      playerWins: [...this.playerWins] as [number, number, number, number],
      playerNames: this.playerNames(),
      playerHands: this.allHands(),
      card: 0,
      boardSide: "posed",
      board: this.boardCopy(),
      gameState: this.state,
      poseModeDoubleSix: this.poseModeDoubleSix,
      wonKey: false,
    };
  }

  async advanceGameIteration(): Promise<GameEvent> {
    switch (this.state) {
      case "wp": {
        this.board = emptyBoard();
        this.poseModeDoubleSix = false;
        this.partnerPosesAllowed = 2;
        this.passCount = 0;
        this.shuffleAndDistribute();
        this.playerTurn = this.lastWinner;
        const ev = this.baseEvent();
        ev.eventType = "playerTurn";
        this.state = "epp";
        return ev;
      }
      case "epp": {
        this.playerTurn = this.lastWinner;
        const card = await this.players[this.playerTurn].poseCard();
        if (
          this.gameType === "partner" &&
          card === PartnerPose &&
          this.partnerPosesAllowed > 0
        ) {
          this.playerTurn = (this.playerTurn + 2) % 4;
          this.lastWinner = this.playerTurn;
          this.partnerPosesAllowed--;
          const ev = this.baseEvent();
          ev.eventType = "playerTurn";
          return ev;
        }
        if (card === EmptyDomino || !this.playerHasCard(this.playerTurn, card)) {
          const ev = this.baseEvent();
          ev.eventType = "null";
          return ev;
        }
        this.updateBoard(card, "posed");
        this.removeCardFromPlayer(card, this.playerTurn);
        const ev = this.baseEvent();
        ev.eventType = "posedCard";
        ev.card = this.board.posedCard;
        ev.boardSide = "posed";
        this.passCount = 0;
        this.lastDominoPlayed = card;
        this.state = "crb";
        return ev;
      }
      case "dsp": {
        this.board = emptyBoard();
        this.poseModeDoubleSix = true;
        this.passCount = 0;
        this.shuffleAndDistribute();
        this.playerTurn = this.findDoubleSixPlayer();
        const ev = this.baseEvent();
        ev.eventType = "playerTurn";
        this.state = "eppds";
        this.lastDominoPlayed = DoubleSixIndex;
        return ev;
      }
      case "eppds": {
        if (this.playerTurn !== -1) {
          const card = await this.players[this.playerTurn].poseDoubleSix();
          if (card !== DoubleSixIndex) break;
          this.updateBoard(card, "posed");
          this.removeCardFromPlayer(card, this.playerTurn);
          this.lastDominoPlayed = card;
        }
        const ev = this.baseEvent();
        ev.eventType = "posedCard";
        ev.card = this.board.posedCard;
        ev.boardSide = "posed";
        this.passCount = 0;
        this.state = "crb";
        return ev;
      }
      case "ept": {
        const turnEv = this.baseEvent();
        turnEv.eventType = "playerTurn";
        if (!this.canPlayCard(this.playerTurn)) {
          turnEv.eventType = "passed";
          this.passCount++;
          if (this.passCount >= 8) {
            turnEv.eventType = "roundInvalid";
            this.state = "crw";
          } else {
            this.state = "crb";
          }
          return turnEv;
        }
        const choice = await this.players[this.playerTurn].playCard(turnEv);
        const { card, boardSide } = choice;
        if (card === EmptyDomino || !this.playerHasCard(this.playerTurn, card)) {
          const ev = this.baseEvent();
          ev.eventType = "null";
          return ev;
        }
        const [s1, s2] = getSuits(card);
        let compatible = false;
        if (
          s1 === this.board.leftSuit ||
          s1 === this.board.rightSuit ||
          s2 === this.board.leftSuit ||
          s2 === this.board.rightSuit
        )
          compatible = true;
        if (!compatible) {
          const ev = this.baseEvent();
          ev.eventType = "null";
          return ev;
        }
        if (
          boardSide === "left" &&
          s1 !== this.board.leftSuit &&
          s2 !== this.board.leftSuit
        ) {
          const ev = this.baseEvent();
          ev.eventType = "null";
          return ev;
        }
        if (
          boardSide === "right" &&
          s1 !== this.board.rightSuit &&
          s2 !== this.board.rightSuit
        ) {
          const ev = this.baseEvent();
          ev.eventType = "null";
          return ev;
        }
        const ev = this.baseEvent();
        ev.eventType = "playedCard";
        ev.card = card;
        ev.boardSide = boardSide;
        this.passCount = 0;
        this.updateBoard(card, boardSide);
        this.removeCardFromPlayer(card, this.playerTurn);
        this.lastDominoPlayed = card;
        ev.playersCardsRemaining = this.allCounts();
        ev.board = this.boardCopy();
        this.state = "crb";
        return ev;
      }
      case "crb": {
        const ev = this.baseEvent();
        if (this.isGameBlocked()) {
          ev.eventType = "roundBlocked";
        }
        this.state = "crw";
        return ev;
      }
      case "crw": {
        const ev = this.baseEvent();
        if (this.passCount >= 8) {
          ev.eventType = "roundDraw";
          this.state = "dsp";
          this.numRounds++;
          this.passCount = 0;
          return ev;
        }
        for (let i = 0; i < 4; i++) {
          if (this.numCardsRemaining(i) <= 0) {
            let key = 0;
            if (this.wonKey(this.lastDominoPlayed)) {
              key = 1;
              ev.wonKey = true;
            }
            this.state = "gwc";
            this.lastWinner = i;
            this.playerWins[i] += 1 + key;
            if (this.gameType === "partner") {
              const p = (i + 2) % 4;
              this.playerWins[p] += 1 + key;
            }
            this.numRounds++;
            ev.player = i;
            ev.playerWins = [...this.playerWins] as [number, number, number, number];
            ev.eventType = "roundWin";
            return ev;
          }
        }
        if (this.isGameBlocked()) {
          let lowestPlayer = 0;
          let lowestCount = this.playerHandPipCount(0);
          let drawed = false;
          for (let i = 1; i < 4; i++) {
            const c = this.playerHandPipCount(i);
            if (c < lowestCount) {
              lowestCount = c;
              lowestPlayer = i;
              drawed = false;
            } else if (c === lowestCount) {
              const partner = (lowestPlayer + 2) % 4;
              if (this.gameType === "partner" && partner !== i) drawed = true;
              else if (this.gameType !== "partner") drawed = true;
            }
          }
          if (!drawed) {
            ev.eventType = "roundWin";
            ev.player = lowestPlayer;
            this.state = "gwc";
            this.lastWinner = lowestPlayer;
            this.playerWins[lowestPlayer]++;
            if (this.gameType === "partner") {
              const p = (lowestPlayer + 2) % 4;
              this.playerWins[p]++;
            }
            this.numRounds++;
            ev.playerWins = [...this.playerWins] as [number, number, number, number];
            return ev;
          }
          ev.eventType = "roundDraw";
          this.state = "dsp";
          this.numRounds++;
          return ev;
        }
        this.advancePlayerTurn();
        ev.eventType = "playerTurn";
        ev.player = this.playerTurn;
        this.state = "ept";
        return ev;
      }
      case "gwc": {
        const ev = this.baseEvent();
        for (let i = 0; i < 4; i++) {
          if (this.playerWins[i] >= this.maxWins) {
            this.state = "idle";
            ev.eventType = "gameWin";
            ev.player = i;
            return ev;
          }
        }
        this.state = "wp";
        return ev;
      }
      case "idle":
      default:
        return this.baseEvent();
    }
    return this.baseEvent();
  }
}
